<script lang="ts" setup>
import { computed } from 'vue'
import { v4 as uuid } from 'uuid'
import ModuleCard from '@/pages/home/components/modules/ModuleCard.vue'
import { theme as brandTheme } from '@/config/brands'

defineProps<{
  config: any
  chapterTheme: any
  statusDisplay: string
  statusDescription: string
  fetching: boolean
  error: any
  recommendations: any[]
  allData: any
  total: number
  pageSize: number
  goToCollection: () => void
  handleBookClick: (book: any) => void
}>()

const pageStyle = computed(() => ({
  backgroundColor: brandTheme.bgGlobal || '#14181F',
}))
</script>

<template>
  <div class="rr-recommend-page h-100dvh overflow-scroll" :style="pageStyle">
    <BasicNavbar />

    <div class="px-15px pb-20px pt-48px">
      <div class="flex justify-center items-center">
        <div class="w-30px h-1px bg-white/20" />
        <div class="mx-4 text-26px font-bold text-n-text-1">
          {{ statusDisplay }}
        </div>
        <div class="w-30px h-1px bg-white/20" />
      </div>

      <div class="mt-4px text-center text-n-text-2">
        {{ statusDescription }}
      </div>

      <ModuleCard
        class="mt-12px"
        :title-text="allData.showName || config.texts.youMightAlsoLike"
        :action-text-value="config.texts.more"
        :show-title="!fetching && !error && !!recommendations.length"
        :show-action="!fetching && !error && total > pageSize"
        card-class="px-3 py-4 bg-n-bg-card rounded-2xl border border-n-border-primary overflow-hidden"
        header-class="mb-2px flex items-baseline justify-between"
        @action="goToCollection"
      >
        <div v-if="fetching" class="w-full py-15px flex items-center justify-center">
          <BasicSpinner class="mx-auto block" />
        </div>

        <div v-else-if="error" class="py-15px text-13px text-center text-n-text-3">
          {{ config.texts.failedToLoad }}
        </div>

        <div v-else class="mt-8px space-y-14px">
          <div v-for="(item, index) in recommendations" :key="index" class="rr-feed-item" @click="handleBookClick(item)">
            <BizBookCover
              :id="uuid()"
              :src="item.contentCoverUrl || ''"
              :extra="{
                module_name: allData.showName,
                module_id: allData.id,
                module_type: allData.columnType,
                author: item.authorInfo?.author,
                ...item,
              }"
              class="rr-cover rounded-10px"
            />

            <div class="min-w-0 flex-1">
              <p class="text-15px lh-21px font-700 line-clamp-2 text-n-text-1">
                {{ item.contentName?.replace(/&nbsp;/g, ' ') ?? '' }}
              </p>
              <p class="mt-8px text-12px lh-18px text-n-text-3 line-clamp-3 break-anywhere">
                {{ item.introduce }}
              </p>
              <BizShowTags v-if="item.tagList && item.tagList.length" :tags="item.tagList" class="mt-9px" />
            </div>
          </div>
        </div>
      </ModuleCard>
    </div>
  </div>
</template>

<style scoped>
.rr-recommend-page {
  background-image: none !important;
}

.rr-feed-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 0 14px !important;
  border: 0 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  cursor: pointer;
}

.rr-feed-item:last-child {
  border-bottom: 0 !important;
  padding-bottom: 0;
}

.rr-cover {
  width: 76px;
  flex-shrink: 0;
}
</style>
